/**
 * Human-readable ids shown in the editing interface (admin-facing only).
 * Courses render as "C{n}", lessons as "L{n}", blocks as "B{n}", and Question
 * Bank questions as "Q{n}", where {n} is the raw auto-increment id. The obfuscated public id remains the value used
 * for routing and API calls.
 */

/**
 * Format a course's human-readable id. Falls back to the obfuscated id when
 * the numeric id is unavailable (e.g. against an older API).
 */
export function formatCourseHumanId(numericId: number | undefined, fallbackId = ''): string {
  return typeof numericId === 'number' ? `C${numericId}` : fallbackId;
}

/** Lesson counterpart of formatCourseHumanId ("L{n}"). */
export function formatLessonHumanId(numericId: number | undefined, fallbackId = ''): string {
  return typeof numericId === 'number' ? `L${numericId}` : fallbackId;
}

/**
 * Format a block's human-readable id. Persisted blocks carry their numeric id
 * as a digit string; unsaved blocks still have client-generated temp ids and
 * return null (no id to show until the first save).
 */
export function formatBlockHumanId(blockId: string): string | null {
  return /^\d+$/.test(blockId) ? `B${blockId}` : null;
}

/** Question Bank counterpart of formatCourseHumanId ("Q{n}"). */
export function formatQuestionHumanId(questionDefinitionId: number): string {
  return `Q${questionDefinitionId}`;
}
